import DLabGrid from "../../components/DLabGrid/DLabGrid";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BookingMeta, singleEventSkeleton } from "../../constants";
import {
  filterParams,
  get4WeekStartAndEndInISOString,
  onlyUnique,
  replaceOffsetWithOther,
  valueOrEmpty,
  formatUserDisplay,
  USER_FORMAT_PRESETS,
  getBookedUserDisplay,
  buildProjectsLabel,
  formatDateTimeInTimezone,
} from "../../utils/helpers/text/index";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { useGraphQLErrorHandling } from "../../components/error-handling";
import { GET_BOOKING_DATA_BY_SITE, GET_BOOKING_ENTRIES_BY_BOOKING_ID } from "../../gql/bookingapi";
import {
  deriveShortDescription,
  MAX_RICH_DESCRIPTION_LENGTH,
  RichDescriptionCellRenderer,
} from "../../components/shared/RichDescription";
import { compose, withApollo } from "react-apollo";
import { connect, useSelector } from "react-redux";
import { selectIsPredOrGredUser } from "../../selectors/user";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import BookingMenuAction from "./BookingMenuAction";
import BookingsPaginationActions from "../../components/shared/BookingsPaginationActions";
import BackdropPortal from "../../components/shared/BackdropPortal";
import moment from "moment";
import BookingDetailRenderer from "../../components/shared/BookingDetailRenderer";
import { APPROVE_BOOKING } from "../../gql/bookingapi/mutations";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { useHistory } from "react-router-dom";
import { BookingContext } from "../booking/context";
import { cloneDeep, uniqBy } from "lodash";
import HazardousRejectDialog from "../hazardous-materials/HazardousRejectDialog";
import AssistanceAssignmentDialog from "../hazardous-materials/AssistanceAssignmentDialog";
import CheckInButton from "../../components/shared/CheckInButton/CheckInButton";
import { ElnLinkRenderer, EN_DASH } from "@digitallab/grid-common-frontend-components";
import { BookingTypeChip } from "../../components/shared/BookingTypeChip";
import { BookingHazardousChip } from "../../components/shared/BookingHazardousChip";
import { getBookingDisplayText } from "../../components/shared/helpFunctions";
import { hasCheckInState } from "../../components/shared/checkInVisibility";
import { hazardousStatusGetter } from "../../utils/helpers/data";
import {
  createDefaultCsvExportParams,
  createDefaultExcelExportParams,
  createClipboardCellProcessor,
  getExportableColumnKeys,
} from "../../utils/helpers/exportValueMapper";
import { DateWithRecurrenceCellRenderer } from "../../utils/helpers/text/DateWithRecurrenceCellRenderer";
import ProjectRenderer from "../equipment-list/ProjectRender";
import { sanitizeFilterModelForGrid } from "../../components/EquipmentList/Service/filterService";
import ChangeBookingEndTimeModal from "../my-booking/ChangeBookingEndTime";
import useChangeEndTimeModal from "../my-booking/ChangeBookingEndTime/hooks/useChangeEndTimeModal";

const serializeFilterModel = (model) => {
  try {
    return JSON.stringify(model || {});
  } catch (_error) {
    return "{}";
  }
};

const BookingList = ({
  client,
  resetAll,
  searchQuery,
  setSearchQuery,
  setSelectedDateRange,
  setDisableResetAll,
  selectedDateRange,
  setDropType,
  setIsDropBooking,
  setEnableResetAll,
  setIsDialogOpen,
  showAdvanceFilter,
  selectedEquipment,
  setSelectedEquipment,
  setOpenMultiDeleteDialog,
  setRowEquipmentId,
  tempRowData,
  setTempRowData,
  setResetAll,
  loadPendingReservations,
  setPopupEnableStatus,
  displayPreset,
  signeeFilter,
  innerTableFilters,
  setInnerTableFilters,
}) => {
  const { nextToken, data, nextData, fetching, dispatchAction } = useContext(PaginationContext);
  const { dispatchAction: bookingDispatchAction } = useContext(BookingContext);

  // Initialize error handling for GraphQL operations
  const { handleGetAllData } = useGraphQLErrorHandling("BookingList");
  const user = useSelector((store) => store.user);
  const users = useSelector((state) => state.users.users);
  const [gridApi, setGridApi] = useState(null);

  // Local state for hazardous/assistance dialogs
  const [openHazardousRejectDialog, setOpenHazardousRejectDialog] = useState(false);
  const [hazardousBookingToReject, setHazardousBookingToReject] = useState(null);
  const [openAssistanceDialog, setOpenAssistanceDialog] = useState(false);
  const [bookingForAssistance, setBookingForAssistance] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // eslint-disable-next-line no-unused-vars, no-unused-vars
  const [status, setStatus] = useState(false);

  const gridRef = useRef();
  const rowStyle = { background: "var(--one-color-cobas-accent-gray-100)" };
  const history = useHistory();

  useEffect(() => {
    if (!fetching) {
      fetchData().then(() => setResetAll(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange, nextData]);

  useEffect(() => {
    if (gridApi) {
      gridRef.current.api.setQuickFilter(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const dateFromFormatter = (params) => {
    return moment(params?.data?.dateFrom).tz(user.timezone).format("DD-MMM-YYYY");
  };
  const dateToFormatter = (params) => {
    return moment(params?.data?.dateTo).tz(user.timezone).format("DD-MMM-YYYY");
  };

  const statusGetter = (params) => {
    const data = params?.data;
    if (!data?.active) {
      return "Cancelled";
    }
    // Check if booking has hazardous materials and is pending approval
    const normalizedStatus = data?.approval?.status?.toString().toUpperCase();
    if (data?.hazardousDeclaration?.hasHazardousMaterials && normalizedStatus === "PENDING") {
      return "Pending";
    }
    return "Active";
  };
  const equipmentGetter = (params) => {
    let equipment = params?.data?.bookedEquipments ?? [];
    const equipmentFromEntries = (params?.data?.bookingEntries ?? [])
      .map((entry) => entry?.equipment?.shortIdentifier)
      .filter(Boolean);
    if (!equipment.length) {
      return "-";
    }
    if (equipmentFromEntries.length) {
      equipment = equipmentFromEntries;
    }
    const result = equipment[0] && equipment[0].length > 10 ? equipment[0].substring(0, 10) + "... " : equipment[0];
    const suffix = equipment.length - 1 > 0 ? `+${equipment.length - 1}` : "";
    return result + suffix;
  };
  const equipmentFilterValuesGetter = (params) => {
    const equipmentFromEntries = (params?.data?.bookingEntries ?? [])
      .map((entry) => entry?.equipment?.shortIdentifier)
      .filter(Boolean);
    if (equipmentFromEntries.length) {
      return equipmentFromEntries;
    }
    return params?.data?.bookedEquipments ?? [];
  };
  const reservedForGetter = (params) => {
    const users = (params?.data?.bookedUsers ?? []).filter(Boolean);
    if (!users.length) {
      return "-";
    }
    const display = getBookedUserDisplay(users[0]);
    const result = display.length > 10 ? display.substring(0, 10) + "... " : display;
    const suffix = users.length - 1 > 0 ? ` +${users.length - 1}` : "";
    return result + suffix;
  };
  const reservedForFilterFormatterGetter = (params) => {
    const users = (params?.data?.bookedUsers ?? []).filter(Boolean);
    if (!users.length) {
      return "-";
    }
    return users.map(getBookedUserDisplay);
  };
  const projectGetter = (params) => {
    const row = params?.data || {};
    return buildProjectsLabel({
      rowRedPandaProjects: row.redPandaProjects,
      userRedPandaProjects: user?.redPandaProjects,
      projectId: row.project,
    });
  };
  const descriptionGetter = (params) => {
    const shortValue = params?.data?.description;
    if (typeof shortValue === "string" && shortValue.trim().length > 0) {
      return shortValue;
    }
    const richValue = params?.data?.richDescription;
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return deriveShortDescription(richValue);
    }
    return valueOrEmpty(shortValue, false, "-");
  };
  const richDescriptionGetter = (params) => {
    const richValue = params?.data?.richDescription;
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return deriveShortDescription(richValue, MAX_RICH_DESCRIPTION_LENGTH);
    }
    const shortValue = params?.data?.description;
    return valueOrEmpty(shortValue, false, "-");
  };

  const fetchData = async () => {
    dispatchAction({
      type: "fetching",
      payload: true,
    });

    let dateFrom, dateTo;

    if (selectedDateRange[0] instanceof Date) {
      const dateFromString = moment(selectedDateRange[0]).format();
      const dateToString = moment(selectedDateRange[1]).format();
      const offset = moment.tz(user.timezone).format("Z");
      dateFrom = moment(replaceOffsetWithOther(dateFromString, offset)).utc().format();
      dateTo = moment(replaceOffsetWithOther(dateToString, offset, true))
        .utc()
        .format();
    } else {
      dateFrom = moment(selectedDateRange[0]).utc().format();
      dateTo = moment(selectedDateRange[1]).utc().format();
    }

    const bookingFilter = {
      and: [{ active: { eq: true } }, { and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }] }],
    };

    /* -- SIGNEE filter ------------------------------------------------------- */
    if (signeeFilter?.userId && signeeFilter.userId !== "null" && signeeFilter.userId !== "") {
      bookingFilter.and.push({ reservedForUsers: { contains: signeeFilter.userId } });
    }

    const { items, nextToken: token } = await handleGetAllData({
      client,
      query: GET_BOOKING_DATA_BY_SITE,
      dataPath: ["data", "getBookingDataBySite"],
      drillData: true,
      variables: {
        filter: JSON.stringify(bookingFilter),
        dateFrom: dateTo,
        site: user.site,
        limit: 1000,
        nextToken,
      },
      cache: true,
    });

    dispatchAction({
      type: "nextToken",
      payload: {
        token,
        data: items,
      },
    });
  };

  const refreshGrid = useCallback(() => {
    const api = gridApi?.api || gridApi;
    if (!api || typeof api.refreshCells !== "function") return;
    api.refreshCells();
  }, [gridApi]);

  const {
    isChangeEndTimeOpen,
    bookingForEndTimeChange,
    handleOpenChangeEndTimeModal: changeEndTimeOpen,
    handleCloseChangeEndTimeModal,
    handleSaveEndTime,
  } = useChangeEndTimeModal({client});

  const handleApprove = useCallback(
    async (booking) => {
      if (booking?.assistanceDeclaration?.requiresAssistance) {
        setBookingForAssistance(booking);
        setIsEditMode(false);
        setOpenAssistanceDialog(true);
      } else {
        try {
          dispatchAction({
            type: "fetching",
            payload: true,
          });

          await client.mutate({
            mutation: APPROVE_BOOKING,
            variables: {
              bookingId: booking.id,
              approval: {
                status: "APPROVED",
                approvedBy: user.user,
                approvedByEmail: user.email,
              },
              createdBy: user.user,
              createdByEmail: user.email,
            },
          });
          // Don't show success here - let the subscription callback handle it
        } catch (error) {
          console.error("Approval error:", error);
          snackbarService.show({
            type: "error",
            message: "Failed to approve booking",
            duration: 3000,
          });
        } finally {
          dispatchAction({
            type: "clearAll",
          });
        }
      }
    },
    [client, user, dispatchAction],
  );

  const handleReject = useCallback((booking) => {
    setHazardousBookingToReject(booking);
    setOpenHazardousRejectDialog(true);
  }, []);

  const actionRender = (params) => {
    return (
      <BookingMenuAction
        params={params}
        handlers={{
          deleteEquipment,
          editBooking,
          setRowEquipmentId,
          setPopupEnableStatus,
          handleApprove,
          handleReject,
          refreshGrid,
          changeEndTimeOpen,
        }}
      />
    );
  };

  const overrideCellRender = (key, defaultObj) => {
    switch (key) {
      case "type":
        defaultObj = {
          ...defaultObj,
          filter: true,
          cellRenderer: (item) =>
            item?.data?.type ? <BookingTypeChip key={item.data.id} item={item.data} /> : EN_DASH,
          filterValueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
          valueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
        };
        break;
      case "checkIn":
        defaultObj = {
          ...defaultObj,
          cellRenderer: (item) => (hasCheckInState(item?.data) ? <CheckInButton key={item.data.id} item={item.data} /> : null),
        };
        break;
      case "dateFrom":
        defaultObj = {
          ...defaultObj,
          checkboxSelection: true,
          showDisabledCheckboxes: true,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateFromFormatter,
          filterValueGetter: dateFromFormatter,
          cellRenderer: DateWithRecurrenceCellRenderer,
        };
        break;
      case "dateTo":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateToFormatter,
          filterValueGetter: dateToFormatter,
        };
        break;
      case "equipment":
        defaultObj = {
          ...defaultObj,
          valueFormatter: equipmentGetter,
          filterValueGetter: equipmentFilterValuesGetter,
        };
        break;
      case "active":
        defaultObj = {
          ...defaultObj,
          valueGetter: statusGetter,
          filterValueGetter: statusGetter,
        };
        break;

      case "hazardous":
        defaultObj = {
          ...defaultObj,
          filter: true,
          cellRenderer: (item) => <BookingHazardousChip key={item.data.id} item={item.data} />,
          filterValueGetter: hazardousStatusGetter,
          valueGetter: hazardousStatusGetter,
        };
        break;

      case "reservedForUsers":
        defaultObj = {
          ...defaultObj,
          valueGetter: reservedForGetter,
          filterValueGetter: reservedForFilterFormatterGetter,
        };
        break;
      case "location":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => params?.data?.locationKey || params?.data?.locationKey,
          filterValueGetter: (params) => params?.data?.locationKey || params?.data?.locationKey,
        };
        break;
      case "project":
        defaultObj = {
          ...defaultObj,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          cellRenderer: ProjectRenderer,
          filterValueGetter: projectGetter,
          cellStyle: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        };
        break;
      case "description":
        defaultObj = {
          ...defaultObj,
          hide: true,
          suppressColumnsToolPanel: true,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          valueGetter: descriptionGetter,
          filterValueGetter: descriptionGetter,
          cellRenderer: RichDescriptionCellRenderer,
        };
        break;
      case "richDescription":
        defaultObj = {
          ...defaultObj,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          valueGetter: richDescriptionGetter,
          filterValueGetter: richDescriptionGetter,
          cellRenderer: RichDescriptionCellRenderer,
        };
        break;
      case "assistent":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => {
            if (!params?.data?.assistent || !params?.data?.assistent[0]) return "-";
            const assistantId = params.data.assistent[0];
            const assistantUser = users?.find((user) => user.userId === assistantId);
            if (assistantUser) {
              return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
            }
            return assistantId;
          },
          filterValueGetter: (params) => {
            if (!params?.data?.assistent || !params?.data?.assistent[0]) return "-";
            const assistantId = params.data.assistent[0];
            const assistantUser = users?.find((user) => user.userId === assistantId);
            if (assistantUser) {
              return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
            }
            return assistantId;
          },
        };
        break;
      case "assistentReason":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => {
            return params?.data?.assistanceDeclaration?.assistanceReason || "-";
          },
          filterValueGetter: (params) => {
            return params?.data?.assistanceDeclaration?.assistanceReason || "-";
          },
        };
        break;
      case "createdBy":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => {
            const creatorId = params?.data?.createdBy;
            if (!creatorId) return "-";
            const creator = users?.find((u) => u.userId === creatorId);
            return creator ? formatUserDisplay(creator, USER_FORMAT_PRESETS.nameOnly) : creatorId;
          },
          filterValueGetter: (params) => {
            const creatorId = params?.data?.createdBy;
            if (!creatorId) return "-";
            const creator = users?.find((u) => u.userId === creatorId);
            return creator ? formatUserDisplay(creator, USER_FORMAT_PRESETS.nameOnly) : creatorId;
          },
        };
        break;
      case "createdAt":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: (params) => formatDateTimeInTimezone(params?.data?.createdAt, user.timezone),
          filterValueGetter: (params) => formatDateTimeInTimezone(params?.data?.createdAt, user.timezone),
        };
        break;
      case "actionButton":
        defaultObj = {
          ...defaultObj,
          width: 0,
          maxWidth: 50,
          field: "actions",
          headerName: "",
          filter: false,
          pinned: "right",
          lockVisible: true,
          suppressColumnsToolPanel: true,
          suppressMenu: true,
          lockPosition: "right",
          cellClass: "action-render",
          cellRenderer: actionRender,
        };

        break;

      case "detailExpander":
        defaultObj = {
          ...defaultObj,
          cellRenderer: "agGroupCellRenderer",
          pinned: "left",
        };

        break;
      case "elnId":
        defaultObj = {
          ...defaultObj,
          filterParams: {
            comparator: (a, b) => a.localeCompare(b),
            defaultToNothingSelected: true,
          },
          comparator: (a, b) => {
            const valueA = a?.name || EN_DASH;
            const valueB = b?.name || EN_DASH;
            return valueA.localeCompare(valueB);
          },
          filterValueGetter: ({ data }) => data?.elnId?.name || EN_DASH,
          valueFormatter: (params) => params?.value,
          cellRenderer: (item) => <ElnLinkRenderer elnExperiment={item.value} />,
        };
        break;
      default:
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => valueOrEmpty(params?.data[key] ?? "-", false, "-"),
          filterValueGetter: (params) => valueOrEmpty(params?.data[key] ?? "-", false),
        };
        break;
    }
    return defaultObj;
  };

  const tableColumn = useMemo(() => {
    const isAdmin = user?.roles?.some((role) => role === "BookingAdmin");
    const isPredOrGredUser = selectIsPredOrGredUser(user);
    const tableColumn = [];
    for (const key in BookingMeta.fields) {
      if (BookingMeta.fields[key].adminOnly && !isAdmin) continue;
      if (BookingMeta.fields[key].siteRestricted && !isPredOrGredUser) continue;
      let defaultObj = {
        headerName: BookingMeta.fields[key].text,
        field: key,
        sortable: true,
        filter: true,
        floatingFilter: true,
        resizable: true,
        headerTooltip: BookingMeta.fields[key].text,
        width: BookingMeta.fields[key].width,
        maxWidth: BookingMeta.fields[key].maxWidth,
        minWidth: BookingMeta.fields[key].minWidth,
      };
      defaultObj = overrideCellRender(key, defaultObj);
      tableColumn.push(defaultObj);
    }
    return tableColumn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BookingMeta.fields, showAdvanceFilter, user]);

  const onGridReady = useCallback((params) => {
    setGridApi(params);
  }, []);

  useEffect(() => {
    if (!gridApi?.api || !innerTableFilters) return;
    if (Object.keys(innerTableFilters).length === 0) return;
    const sanitizedFilters = sanitizeFilterModelForGrid(gridApi.api, innerTableFilters);
    const currentFilters = sanitizeFilterModelForGrid(gridApi.api, gridApi.api.getFilterModel() || {});
    if (serializeFilterModel(currentFilters) === serializeFilterModel(sanitizedFilters)) {
      return;
    }
    gridApi.api.setFilterModel(sanitizedFilters);
  }, [gridApi, innerTableFilters]);

  const attr = useMemo(
    () => ({
      height: "100%",
      columnDefs: tableColumn,
      animateRows: true,
      rowExport: false,
      includeHiddenColumnsInQuickFilter: true,
      defaultToolPanel: "filters",
      allowContextMenuWithControlKey: true,
      getContextMenuItems: (params) => {
        return [
          "copy",
          "copyWithHeaders",
          "copyWithGroupHeaders",
          "separator",
          {
            name: "Export",
            subMenu: [
              {
                name: "Export Selected Rows",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
              "separator",
              {
                name: "Export All (Filtered)",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                        onlySelected: false,
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                        onlySelected: false,
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
            ],
          },
        ];
      },
      defaultCsvExportParams: createDefaultCsvExportParams({}, {}, user),
      defaultExcelExportParams: createDefaultExcelExportParams({}, {}, user),
      processCellForClipboard: createClipboardCellProcessor({}, user),
      hiddenByDefault: true,
      rowData: tempRowData,
      onGridReady,
    }),
    [tableColumn, tempRowData, user, onGridReady],
  );
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      suppressMenu: true,
      sortable: true,
    };
  }, []);

  useEffect(() => {
    if (gridApi && resetAll) {
      gridRef.current.api.setFilterModel();
      setInnerTableFilters && setInnerTableFilters({});
      setSearchQuery("");
      setSelectedEquipment([]);
      setSelectedDateRange([
        get4WeekStartAndEndInISOString("start", user.timezone),
        get4WeekStartAndEndInISOString("end", user.timezone),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAll]);

  const isRowSelectable = useMemo(() => {
    return (params) => {
      const node = params.level;
      const isGroupRow = node === 0;
      const isActive = isBookingAdmin
        ? params.data.active === true
        : params.data.active === true &&
          moment(new Date(params?.data?.dateFrom)).tz(user.timezone).format("YYYY-MM-DD HH:mm") >
            moment(new Date()).tz(user.timezone).format("YYYY-MM-DD HH:mm");
      if (isGroupRow) {
        params.selectable = isActive;
      }
      return isGroupRow && isActive;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let tempArray = [];
    if (tempRowData.length > 0) {
      tempArray = [...tempArray, ...tempRowData];
    }
    if (data.length > 0) {
      tempArray = [...tempArray, ...data];
    }
    const uniqData = uniqBy(tempArray, "id");
    setTempRowData(uniqData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const scrollEvent = () => {
    const container = document.querySelector(".grid-container");
    if (Math.ceil(container.clientHeight + container.scrollTop) >= container.scrollHeight && nextToken) {
      dispatchAction({ type: "nextData" });
    }
  };

  const deleteEquipment = ({ type = "cancel" } = {}) => {
    setIsDialogOpen(true);
    setDropType(type);
    setIsDropBooking(true);
  };

  const editBooking = async (editData) => {
    const data = cloneDeep(editData);
    const currentTimeString = moment.tz(user.timezone).utc().format();
    dispatchAction({
      type: "fetching",
      payload: true,
    });
    const { items } = await handleGetAllData({
      client,
      query: GET_BOOKING_ENTRIES_BY_BOOKING_ID,
      drillData: true,
      fetchPolicy: "no-cache",
      variables: {
        bookingId: data.id,
        nextToken: null,
        filter: { active: { eq: true } },
      },
      dataPath: ["data", "bookingEntriesByBookingId"],
      cache: true,
    });

    if (items.length > 0) {
      const keyArray = Object.keys(singleEventSkeleton);
      data["bookingEntries"] = items.map((item) => {
        let tempObj = {};
        keyArray.forEach((key) => {
          tempObj = {
            ...tempObj,
            [key]: item[key] ?? null,
          };
        });
        tempObj = {
          ...tempObj,
          inventoryId: item.resource,
          equipment: item.equipment,
          title: item.type,
          description: item?.description,
          elnId: item?.elnId,
          project: item?.project,
          createdBy: item?.createdBy,
          reservedForEmails: item?.reservedForEmails,
        };
        return tempObj;
      });
      if (items[0].elnId) data.elnId = items[0].elnId;
    }

    bookingDispatchAction({
      type: "editBookingData",
      payload: {
        isEditMode: true,
        forceMyBookingsRedirect: true,
        editBookingData: data,
        isOngoing: currentTimeString > data.dateFrom && currentTimeString < data.dateTo,
      },
    });

    const tempBookingEntries = data?.bookingEntries.map((item) => {
      return {
        ...item,
        inventoryId: item.resource,
        isEditEventFlag: true,
        isOngoingEventFlag:
          (currentTimeString > item.end && currentTimeString > item.start) ||
          (currentTimeString > item.start && currentTimeString < item.end),
        editable: currentTimeString < item.end,
        title: item?.type,
      };
    });

    loadPendingReservations(tempBookingEntries);
    if (data) history.push("/");
  };

  const onRowSelected = useCallback(
    (params) => {
      if (params?.source === "checkboxSelected" || params?.source === "rowClicked") {
        const id = params?.data?.id;
        if (!selectedEquipment.includes(id)) {
          setSelectedEquipment((prevState) => {
            return onlyUnique([...prevState, id]);
          });
        } else {
          let uniqueList = onlyUnique(selectedEquipment);
          const index = uniqueList.indexOf(id);
          if (index > -1) {
            let temp = uniqueList;
            temp.splice(index, 1);
            setSelectedEquipment(() => {
              return temp;
            });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedEquipment],
  );

  const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");

  const handleDescriptionUpdate = useCallback(async () => {
    dispatchAction({ type: "clearAll" });
  }, [dispatchAction]);

  const handleAssistanceUpdate = useCallback(async () => {
    dispatchAction({ type: "clearAll" });
  }, [dispatchAction]);

  const detailCellRenderer = (props) => (
    <BookingDetailRenderer
      {...props}
      key={JSON.stringify(displayPreset?.label)}
      isAdminContext={isBookingAdmin}
      displayPreset={displayPreset}
      setOpenHazardousRejectDialog={setOpenHazardousRejectDialog}
      setHazardousBookingToReject={setHazardousBookingToReject}
      setOpenAssistanceDialog={setOpenAssistanceDialog}
      setBookingForAssistance={setBookingForAssistance}
      setIsEditMode={setIsEditMode}
      allowAssistanceEdit={isBookingAdmin}
      onDescriptionUpdate={handleDescriptionUpdate}
      onAssistanceUpdateComplete={handleAssistanceUpdate}
      status={status}
      setStatus={setStatus}
    />
  );

  const handleGridFilterChanged = useCallback(
    (params) => {
      const filters = sanitizeFilterModelForGrid(params.api, params.api.getFilterModel());
      if (setInnerTableFilters && serializeFilterModel(filters) !== serializeFilterModel(innerTableFilters)) {
        setInnerTableFilters(filters);
      }
      const hasFilters = Object.keys(filters).length > 0;

      if (params?.afterDataChange === false) {
        setEnableResetAll(hasFilters);
      }

      if (Object.keys(filters).length > 0) {
        setDisableResetAll(false);
      } else {
        setDisableResetAll(true);
      }
    },
    [innerTableFilters, setDisableResetAll, setEnableResetAll, setInnerTableFilters],
  );

  return (
    <>
      <BackdropPortal show={fetching} />

      <div
        className="grid-container"
        onScroll={scrollEvent}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "415px",
        }}
      >
        <DLabGrid
          {...attr}
          pagination={false}
          suppressPaginationPanel={false}
          defaultColDef={defaultColDef}
          masterDetail={true}
          rowSelection={"multiple"}
          suppressRowClickSelection={true}
          detailCellRenderer={detailCellRenderer}
          detailRowAutoHeight={true}
          autoGroupColumnDef={"autoGroupColumnDef"}
          onRowSelected={onRowSelected}
          isRowSelectable={isRowSelectable}
          gridRef={gridRef}
          rowStyle={rowStyle}
          onFilterChanged={handleGridFilterChanged}
        />
      </div>
      <BookingsPaginationActions
        selectedCount={selectedEquipment?.length}
        onCancelClick={() => setOpenMultiDeleteDialog(true)}
      />

      {/* Hazardous Rejection Dialog */}
      {openHazardousRejectDialog && (
        <HazardousRejectDialog
          openRejectDialog={openHazardousRejectDialog}
          setOpenRejectDialog={setOpenHazardousRejectDialog}
          booking={hazardousBookingToReject}
          user={user}
          force={true}
          status={status}
          setStatus={setStatus}
        />
      )}

      {/* Assistance Assignment Dialog */}
      {openAssistanceDialog && (
        <AssistanceAssignmentDialog
          open={openAssistanceDialog}
          onClose={() => {
            setOpenAssistanceDialog(false);
            setBookingForAssistance(null);
            setIsEditMode(false);
          }}
          booking={bookingForAssistance}
          client={client}
          onApprovalComplete={() => {
            setOpenAssistanceDialog(false);
            setBookingForAssistance(null);
            setIsEditMode(false);
            // Allow UI to refresh after approval
            setTimeout(() => {
              refreshGrid();
              dispatchAction({
                type: "refresh",
              });
            }, 500);
          }}
          user={user}
          setStatus={setStatus}
          isEditMode={isEditMode}
        />
      )}

      <ChangeBookingEndTimeModal
        isOpen={isChangeEndTimeOpen}
        onClose={handleCloseChangeEndTimeModal}
        booking={bookingForEndTimeChange}
        onSave={handleSaveEndTime}
        initialSelectedResourceId={bookingForEndTimeChange?.resource}
      />
    </>
  );
};

export default compose(
  connect(null, {
    loadPendingReservations: loadPendingReservationsAction,
  }),
  withApollo,
)(BookingList);
